import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  ScreenTransitioner,
  ScreenTransitionerHandle,
} from "../ScreenTransitioner/ScreenTransitioner";
import { DetailsScreen } from "./DetailsScreen";
import { EmailScreen } from "./EmailScreen";
import { GenderPreferenceScreen } from "./GenderPreferenceScreen";
import { OTPScreen } from "./OTPScreen";
import { ProfilePhotosScreen } from "./ProfilePhotosScreen";
import { useStoreData } from "../../lib/store/store";
import { ScreenTransitionDirection } from "../ScreenTransitioner/ScreenTransitionDirection";

export type AuthScreenName = "details" | "genderPreference" | "profilePhotos";
export type AuthStartScreen = "email" | AuthScreenName;

interface AuthenticationFormProps {
  confirmLabel: string;
  /** The email screen's button, when it should not read `confirmLabel`. */
  sendCodeLabel?: string;
  onClose?: () => void;
  onComplete: () => void;
  onAuthenticated?: () => void | Promise<void>;
  source?: string;
  screens?: AuthScreenName[];
  defaultEmail?: string;
  /** Sign in only as `defaultEmail`: the email field is read-only. */
  lockEmail?: boolean;
  startScreen?: AuthStartScreen;
}

export interface AuthenticationFormHandle {
  setIndex: (
    activeIndex: string,
    options?: {
      direction?: ScreenTransitionDirection;
      noAnimate?: boolean;
    }
  ) => void;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
}

const DEFAULT_SCREENS: AuthScreenName[] = ["details", "genderPreference"];

export const AuthenticationForm = forwardRef<
  AuthenticationFormHandle,
  AuthenticationFormProps
>(
  (
    {
      confirmLabel,
      sendCodeLabel,
      onClose,
      onComplete,
      onAuthenticated,
      source,
      screens = DEFAULT_SCREENS,
      defaultEmail,
      lockEmail,
      startScreen: startScreenProp,
    },
    ref
  ) => {
    const storeData = useStoreData();
    const [email, setEmail] = useState(defaultEmail || "");
    const screenTransitionerRef = useRef<ScreenTransitionerHandle>(null);
    useImperativeHandle(ref, () => ({
      setIndex: async (activeIndex, options) => {
        screenTransitionerRef.current?.setIndex(activeIndex, options);
      },
      setEmail,
    }));

    const hasScreen = (name: AuthScreenName) => screens.includes(name);

    const getFirstIncompleteScreen = (user: {
      birth_date?: string | null;
      gender?: string | null;
      gender_preference?: string | null;
      friends_only?: boolean | null;
    }): string | null => {
      if (hasScreen("details") && (!user.birth_date || !user.gender))
        return "details";
      // Friends only answers the dating-preference step: someone who isn't
      // dating has no preference to give, and without this they'd be sent
      // back to this screen on every sign-in with no way to satisfy it.
      if (
        hasScreen("genderPreference") &&
        !user.gender_preference &&
        !user.friends_only
      )
        return "genderPreference";
      if (hasScreen("profilePhotos")) return "profilePhotos";
      return null;
    };

    const isLoggedIn = !!storeData.auth.token && !!storeData.user;

    const startIndex = useMemo(() => {
      if (startScreenProp) return startScreenProp;
      if (isLoggedIn && storeData.user) {
        return getFirstIncompleteScreen(storeData.user) || null;
      }
      return "email";
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (startIndex === null) {
        onComplete();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextScreenAfterDetails = (): string | null => {
      if (
        hasScreen("genderPreference") &&
        !storeData.user?.gender_preference &&
        !storeData.user?.friends_only
      )
        return "genderPreference";
      if (hasScreen("profilePhotos")) return "profilePhotos";
      return null;
    };

    const getNextScreenAfterGenderPreference = (): string | null => {
      if (hasScreen("profilePhotos")) return "profilePhotos";
      return null;
    };

    const goToOrComplete = (nextScreen: string | null) => {
      if (nextScreen) {
        screenTransitionerRef.current?.setIndex(nextScreen);
      } else {
        onComplete();
      }
    };

    const screenMap: Record<string, React.ReactNode> = {
      email: (
        <EmailScreen
          confirmLabel={sendCodeLabel ?? confirmLabel}
          defaultEmail={defaultEmail}
          lockEmail={lockEmail}
          onNext={(submittedEmail) => {
            screenTransitionerRef.current?.setIndex("otp");
            setEmail(submittedEmail);
          }}
          onClose={onClose}
        />
      ),
      otp: (
        <OTPScreen
          confirmLabel={confirmLabel}
          email={email}
          onPrevious={() =>
            screenTransitionerRef.current?.setIndex("email", {
              direction: ScreenTransitionDirection.Left,
            })
          }
          onNext={(user) => goToOrComplete(getFirstIncompleteScreen(user))}
          onAuthenticated={onAuthenticated}
          source={source}
        />
      ),
    };

    if (hasScreen("details")) {
      screenMap.details = (
        <DetailsScreen
          onClose={onClose}
          onNext={() => goToOrComplete(getNextScreenAfterDetails())}
        />
      );
    }

    if (hasScreen("genderPreference")) {
      screenMap.genderPreference = (
        <GenderPreferenceScreen
          onClose={onClose}
          onComplete={() =>
            goToOrComplete(getNextScreenAfterGenderPreference())
          }
        />
      );
    }

    if (hasScreen("profilePhotos")) {
      screenMap.profilePhotos = (
        <ProfilePhotosScreen onClose={onClose} onComplete={onComplete} />
      );
    }

    if (!startIndex) return null;

    return (
      <ScreenTransitioner
        ref={screenTransitionerRef}
        minH="100%"
        screens={screenMap}
        startIndex={startIndex}
      />
    );
  }
);
